

import React, { useState, useEffect, useRef } from "react";
import {
  sendPasswordResetEmail,
  GoogleAuthProvider,
  linkWithPopup,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { auth } from "../../config/firebaseConfig";
import {
  Box, Typography, Avatar, TextField, Button, CircularProgress, IconButton, Divider, Skeleton, Tooltip
} from "@mui/material";
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import GoogleIcon from '@mui/icons-material/Google';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useUserProfile } from "../../hooks/useUserProfile";
import { getApiUrl, getApiHeaders } from "../../config/api";

// Format date string to Philippine Time (GMT+8)
function formatToPHTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }) + " GMT+8";
}

const Profile = () => {
  // Use the shared user profile hook
  const {
    displayName: hookDisplayName,
    email: hookEmail,
    photoURL: hookPhotoURL,
    verified,
    lastLogin,
    createdAt,
    providerData,
    loading: loadingUser
  } = useUserProfile();

  // Local editable state
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [googleLinked, setGoogleLinked] = useState(false);
  const [googleMsg, setGoogleMsg] = useState("");
  const [verifying, setVerifying] = useState(false); // for email verification button
  const fileInputRef = useRef();

  // Sync hook data to local state
  useEffect(() => {
    setDisplayName(hookDisplayName);
    setEmail(hookEmail);
    setPhotoURL(hookPhotoURL);
    setGoogleLinked(providerData.some(p => p.providerId === 'google.com'));
  }, [hookDisplayName, hookEmail, hookPhotoURL, providerData]);

  // Save profile changes (displayName, photo)
  const handleSave = async () => {
    setLoading(true);
    try {
      // Update name and photo in backend
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(getApiUrl("api/me"), {
        method: "PUT",
        headers: getApiHeaders({
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        }),
        body: JSON.stringify({ display_name: displayName, photo_url: photoURL })
      });
      if (res.ok) {
        setSuccess("Profile updated!");
        setEditMode(false);
      } else {
        setSuccess("Failed to update profile");
      }
    } catch (err) {
      setSuccess("Failed to update profile");
    }
    setLoading(false);
    setTimeout(() => setSuccess(""), 2000);
  };

  // Delete/sign out functionality removed (handled elsewhere)

  // Photo upload (preview only, real upload should go to backend or Firebase Storage)
  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoURL(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Password reset
  const handleResetPassword = async () => {
    if (!email) {
      setResetMsg("No email found for this profile.");
      setTimeout(() => setResetMsg(""), 3000);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg("Password reset link sent to your email.");
    } catch (error) {
      setResetMsg(error.message || "Failed to send reset email.");
    }
    setTimeout(() => setResetMsg(""), 4000);
  };

  // Change password UI removed for now (use reset email instead)

  // Google account binding
  const handleBindGoogle = async () => {
    setGoogleMsg("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser, provider);
      setGoogleLinked(true);
      setGoogleMsg("Google account successfully linked!");
    } catch (err) {
      setGoogleMsg("Failed to bind Google account");
    }
    setLoading(false);
  };

  // Email verification
  const handleSendVerification = async () => {
    setVerifying(true);
    try {
      await sendEmailVerification(auth.currentUser);
      setSuccess("Verification email sent!");
    } catch (err) {
      setSuccess("Failed to send verification email");
    }
    setVerifying(false);
    setTimeout(() => setSuccess(""), 2000);
  };

  // --- UI ---
  return (
  <Box sx={{ bgcolor: '#ffff', width: '100%', maxWidth: 420, borderRadius: 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', p: 0, m: 0 }}>
      <Typography variant="h5" sx={{ textAlign: 'left', mb: 1.5, mt: 0.5, color: '#516d4f', fontWeight: 600 }}>Profile</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 1, gap: 2 }}>
        <Box sx={{ position: 'relative', minWidth: 64, minHeight: 64 }}>
          {loadingUser ? (
            <Skeleton variant="circular" width={64} height={64} />
          ) : (
            <Avatar src={photoURL} sx={{ width: 64, height: 64 }} />
          )}
          {editMode && !loadingUser && (
            <IconButton
              color="primary"
              component="span"
              sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'white', p: 0.5 }}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <PhotoCamera fontSize="small" />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
              />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
          {editMode && !loadingUser ? (
            <TextField
              label="Name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              size="small"
              sx={{ mb: 0.5, width: 180 }}
            />
          ) : loadingUser ? (
            <Skeleton variant="text" width={100} height={28} sx={{ mb: 0.5 }} />
          ) : (
            <Typography variant="h6" sx={{ fontSize: 18 }}>{displayName || "No Name"}</Typography>
          )}
          {editMode && !loadingUser ? (
            <TextField
              label="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              size="small"
              sx={{ mb: 0.5, width: 180 }}
            />
          ) : loadingUser ? (
            <Skeleton variant="text" width={150} height={20} sx={{ mb: 0.5 }} />
          ) : (
            <Typography variant="body2" color="text.secondary">{email || "No Email"}</Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 1, borderColor: '#e1ecc8' }} />
      {/* Provider Info & Google Account Section */}
      <Box sx={{ mb: 1, textAlign: 'left' }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 500, color: '#516d4f' }}>
          Linked Accounts
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {providerData.map((prov, idx) => (
            <Tooltip key={prov.providerId + idx} title={prov.providerId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e1ecc8', borderRadius: 2, px: 1, py: 0.2, bgcolor: '#f7fbe8' }}>
                {prov.providerId === 'google.com' && <GoogleIcon color="primary" fontSize="small" />}
                {prov.providerId === 'password' && <LockResetIcon color="action" fontSize="small" />}
                <Typography variant="body2" sx={{ fontSize: 13 }}>{prov.providerId.replace('.com','')}</Typography>
              </Box>
            </Tooltip>
          ))}
          {!googleLinked && (
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleBindGoogle} 
              disabled={loading} 
              sx={{ 
                ml: 1,
                borderColor: '#a0c49d',
                color: '#516d4f',
                '&:hover': {
                  borderColor: '#8ab287',
                  backgroundColor: '#f7fbe8',
                },
              }}
            >
              Bind Google
            </Button>
          )}
        </Box>
        {googleMsg && <Typography color={googleMsg.includes('success') ? 'success.main' : 'error.main'} sx={{ mt: 0.5, fontSize: 13 }}>{googleMsg}</Typography>}
      </Box>
      <Divider sx={{ my: 1, borderColor: '#e1ecc8' }} />
      {/* Account Info Section */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: 15, color: '#516d4f' }}>Account Info</Typography>
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Created: {formatToPHTime(createdAt)}</Typography>
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Last Login: {formatToPHTime(lastLogin)}</Typography>
        <Typography variant="body2" color={verified ? 'success.main' : 'warning.main'} sx={{ fontSize: 13 }}>
          {verified ? 'Email Verified' : 'Email Not Verified'}
          {!verified && (
            <Button 
              size="small" 
              variant="outlined" 
              sx={{ 
                ml: 1,
                borderColor: '#a0c49d',
                color: '#516d4f',
                '&:hover': {
                  borderColor: '#8ab287',
                  backgroundColor: '#f7fbe8',
                },
              }} 
              onClick={handleSendVerification} 
              disabled={verifying}
            >
              {verifying ? <CircularProgress size={14} /> : 'Send Verification Email'}
            </Button>
          )}
        </Typography>
      </Box>
      <Divider sx={{ my: 1, borderColor: '#e1ecc8' }} />
      {/* Actions Section */}
      {editMode ? (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{ 
              width: 100,
              backgroundColor: '#a0c49d',
              color: '#fbffe8',
              '&:hover': {
                backgroundColor: '#8ab287',
              },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fbffe8' }} /> : "Save"}
          </Button>
          <Button 
            variant="text" 
            onClick={() => setEditMode(false)} 
            disabled={loading} 
            sx={{ 
              width: 80,
              color: '#516d4f',
              '&:hover': {
                backgroundColor: '#f7fbe8',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          <Button
            variant="contained"
            onClick={() => setEditMode(true)}
            sx={{ 
              width: '100%',
              boxShadow: 1, 
              borderRadius: 2, 
              textTransform: 'none', 
              fontWeight: 500,
              backgroundColor: '#a0c49d',
              color: '#fbffe8',
              '&:hover': {
                backgroundColor: '#8ab287',
              },
            }}
            startIcon={<EditIcon />}
          >
            Edit Profile
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetPassword}
            sx={{ 
              width: '100%',
              borderRadius: 2, 
              textTransform: 'none', 
              fontWeight: 500,
              borderColor: '#a0c49d',
              color: '#516d4f',
              '&:hover': {
                borderColor: '#8ab287',
                backgroundColor: '#f7fbe8',
              },
            }}
          >
            Send Password Reset Email
          </Button>
        </Box>
      )}
      {/* Change Password Dialog removed */}
      {success && <Typography color="success.main" sx={{ mt: 1 }}>{success}</Typography>}
      {resetMsg && <Typography color="info.main" sx={{ mt: 1 }}>{resetMsg}</Typography>}
    </Box>
  );
};

export default Profile;
